import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Fab,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TablePagination,
  Tooltip
} from '@mui/material';
import {
  Add,
  Delete,
  Launch,
  Language,
  Schedule,
  TrendingUp,
  Search,
  Clear,
  Edit,
  Extension,
  Refresh,
  CloudDownload,
  AccountCircle,
  FilterList,
  Close,
  CheckCircle,
  Error,
  PlayArrow,
  Minimize,
  ViewModule,
  TableView
} from '@mui/icons-material';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import WordPressSyncButton from '../components/WordPressSyncButton';

const AllSites = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Initialize filters from localStorage or defaults
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem('allSites_searchQuery') || '';
  });
  const [pluginFilter, setPluginFilter] = useState(() => {
    return localStorage.getItem('allSites_pluginFilter') || 'all';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('allSites_statusFilter') || 'all';
  });
  
  // View toggle state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('allSites_viewMode') || 'grid';
  });
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, site: null });
  const [checkingPlugin, setCheckingPlugin] = useState({});
  const [downloadingPlugin, setDownloadingPlugin] = useState(false);
  const [bulkCheckModal, setBulkCheckModal] = useState({ open: false, progress: [], checking: false, minimized: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(localStorage.getItem('allSites_rowsPerPage')) || 25);

  useEffect(() => {
    fetchSites();
  }, []);

  // Reapply filters when sites data changes
  useEffect(() => {
    if (sites.length > 0) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  // Get unique account statuses for filter dropdown
  const getAvailableStatuses = () => {
    const statuses = [...new Set(sites.map(site => site.account_status).filter(Boolean))];
    return statuses.sort();
  };

  // Helper function to get status color scheme
  const getStatusColors = (status) => {
    const statusLower = status?.toLowerCase();
    
    switch (statusLower) {
      case 'active':
        return {
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.08)',
          bgcolor: 'rgba(76, 175, 80, 0.02)',
          hoverShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
          chipBg: 'success.light',
          chipColor: 'success.dark',
          chipBorder: 'success.main',
          iconColor: 'success.main'
        };
      case 'live':
      case 'live - change freeze':
      case 'live - in redesign':
        return {
          boxShadow: '0 2px 8px rgba(56, 142, 60, 0.1)',
          bgcolor: 'rgba(56, 142, 60, 0.03)',
          hoverShadow: '0 8px 24px rgba(56, 142, 60, 0.18)',
          chipBg: 'success.main',
          chipColor: 'white',
          chipBorder: 'success.dark',
          iconColor: 'success.dark'
        };
      case 'implementation':
        return {
          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.08)',
          bgcolor: 'rgba(33, 150, 243, 0.02)',
          hoverShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
          chipBg: 'info.light',
          chipColor: 'info.dark',
          chipBorder: 'info.main',
          iconColor: 'info.main'
        };
      case 'cancelled':
      case 'notified to cancel':
        return {
          boxShadow: '0 2px 8px rgba(244, 67, 54, 0.08)',
          bgcolor: 'rgba(244, 67, 54, 0.02)',
          hoverShadow: '0 8px 24px rgba(244, 67, 54, 0.15)',
          chipBg: 'error.light',
          chipColor: 'error.dark',
          chipBorder: 'error.main',
          iconColor: 'error.main'
        };
      case 'stop work':
      case 'stalled':
        return {
          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.12)',
          bgcolor: 'rgba(211, 47, 47, 0.03)',
          hoverShadow: '0 8px 24px rgba(211, 47, 47, 0.2)',
          chipBg: 'error.main',
          chipColor: 'white',
          chipBorder: 'error.dark',
          iconColor: 'error.dark'
        };
      case 'collections':
        return {
          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)',
          bgcolor: 'rgba(255, 152, 0, 0.02)',
          hoverShadow: '0 8px 24px rgba(255, 152, 0, 0.18)',
          chipBg: 'warning.main',
          chipColor: 'white',
          chipBorder: 'warning.dark',
          iconColor: 'warning.dark'
        };
      case 'other services':
        return {
          boxShadow: '0 2px 8px rgba(156, 39, 176, 0.08)',
          bgcolor: 'rgba(156, 39, 176, 0.02)',
          hoverShadow: '0 8px 24px rgba(156, 39, 176, 0.15)',
          chipBg: 'secondary.light',
          chipColor: 'secondary.dark',
          chipBorder: 'secondary.main',
          iconColor: 'secondary.main'
        };
      default:
        return {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          bgcolor: 'background.paper',
          hoverShadow: '0 8px 24px rgba(24, 99, 220, 0.12)',
          chipBg: 'grey.200',
          chipColor: 'text.secondary',
          chipBorder: 'grey.400',
          iconColor: 'text.secondary'
        };
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data);
      setFilteredSites(response.data);
    } catch (error) {
      setError('Failed to fetch sites');
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (searchQ = searchQuery, pluginF = pluginFilter, statusF = statusFilter) => {
    let filtered = [...sites];

    // Apply search filter
    if (searchQ.trim()) {
      filtered = filtered.filter(site =>
        site.rootdomain.toLowerCase().includes(searchQ.toLowerCase()) ||
        site.website_url.toLowerCase().includes(searchQ.toLowerCase()) ||
        site.account_name?.toLowerCase().includes(searchQ.toLowerCase())
      );
    }

    // Apply plugin filter
    if (pluginF !== 'all') {
      if (pluginF === 'installed') {
        filtered = filtered.filter(site => site.plugin_installed === true);
      } else if (pluginF === 'not_installed') {
        filtered = filtered.filter(site => site.plugin_installed === false || site.plugin_installed === null);
      }
    }

    // Apply status filter
    if (statusF !== 'all') {
      filtered = filtered.filter(site => 
        site.account_status && site.account_status.toLowerCase() === statusF.toLowerCase()
      );
    }

    setFilteredSites(filtered);
    setPage(0);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    localStorage.setItem('allSites_searchQuery', query);
    applyFilters(query, pluginFilter, statusFilter);
  };

  const handlePluginFilter = (filter) => {
    setPluginFilter(filter);
    localStorage.setItem('allSites_pluginFilter', filter);
    applyFilters(searchQuery, filter, statusFilter);
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
    localStorage.setItem('allSites_statusFilter', filter);
    applyFilters(searchQuery, pluginFilter, filter);
  };

  const clearSearch = () => {
    setSearchQuery('');
    localStorage.setItem('allSites_searchQuery', '');
    applyFilters('', pluginFilter, statusFilter);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setPluginFilter('all');
    setStatusFilter('all');
    // Clear localStorage
    localStorage.setItem('allSites_searchQuery', '');
    localStorage.setItem('allSites_pluginFilter', 'all');
    localStorage.setItem('allSites_statusFilter', 'all');
    setFilteredSites(sites);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      localStorage.setItem('allSites_viewMode', newViewMode);
    }
  };

  const handleDeleteSite = async (siteId) => {
    try {
      await api.delete(`/sites/${siteId}`);
      const updatedSites = sites.filter(site => site.id !== siteId);
      setSites(updatedSites);
      // Reapply filters with updated sites
      setTimeout(() => {
        setSites(updatedSites);
        applyFilters();
      }, 0);
      setDeleteDialog({ open: false, site: null });
    } catch (error) {
      setError('Failed to delete site');
      console.error('Error deleting site:', error);
    }
  };

  const openDeleteDialog = (site) => {
    setDeleteDialog({ open: true, site });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, site: null });
  };

  const handleCheckPlugin = async (siteId) => {
    setCheckingPlugin(prev => ({ ...prev, [siteId]: true }));
    setError('');

    try {
      const response = await api.post(`/sites/${siteId}/plugin-check`);
      const result = response.data;
      
      if (result.success) {
        // Update the site in the local state
        const updatedSites = sites.map(site => 
          site.id === siteId 
            ? { 
                ...site, 
                plugin_installed: result.plugin_installed,
                plugin_version: result.plugin_version,
                plugin_last_checked: new Date().toISOString()
              }
            : site
        );
        setSites(updatedSites);
        // Reapply filters with updated sites
        setTimeout(() => {
          setSites(updatedSites);
          applyFilters();
        }, 0);
      } else {
        setError('Failed to check plugin status');
      }
    } catch (error) {
      setError('Network error while checking plugin status');
      console.error('Error checking plugin:', error);
    } finally {
      setCheckingPlugin(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const handleDownloadPlugin = async () => {
    setDownloadingPlugin(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/sites/download-plugin`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download plugin');
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nkp-control-panel.zip';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      setError('Failed to download plugin');
      console.error('Error downloading plugin:', error);
    } finally {
      setDownloadingPlugin(false);
    }
  };

  const handleBulkPluginCheck = () => {
    const sitesToCheck = filteredSites.filter(site => 
      site.plugin_installed === false || site.plugin_installed === null
    );

    if (sitesToCheck.length === 0) {
      setError('No sites need plugin checking');
      return;
    }

    // Initialize progress tracking
    const progress = sitesToCheck.map(site => ({
      siteId: site.id,
      siteName: site.rootdomain,
      status: 'pending', // pending, checking, success, error
      message: 'Waiting to check...',
      pluginInstalled: null,
      pluginVersion: null
    }));

    setBulkCheckModal({ open: true, progress, checking: false, minimized: false });
  };

  const startBulkPluginCheck = async () => {
    setBulkCheckModal(prev => ({ ...prev, checking: true }));
    setError('');

    const sitesToCheck = bulkCheckModal.progress.filter(item => item.status === 'pending');

    for (let i = 0; i < sitesToCheck.length; i++) {
      const siteItem = sitesToCheck[i];
      
      // Update progress to show current site being checked
      setBulkCheckModal(prev => ({
        ...prev,
        progress: prev.progress.map(item =>
          item.siteId === siteItem.siteId
            ? { ...item, status: 'checking', message: 'Checking plugin status...' }
            : item
        )
      }));

      try {
        const response = await api.post(`/sites/${siteItem.siteId}/plugin-check`);
        const result = response.data;

        if (result.success) {
          // Update progress with success
          setBulkCheckModal(prev => ({
            ...prev,
            progress: prev.progress.map(item =>
              item.siteId === siteItem.siteId
                ? {
                    ...item,
                    status: 'success',
                    message: result.plugin_installed 
                      ? `Plugin found (v${result.plugin_version || 'Unknown'})`
                      : 'Plugin not installed',
                    pluginInstalled: result.plugin_installed,
                    pluginVersion: result.plugin_version
                  }
                : item
            )
          }));

          // Update the main sites list
          setSites(prevSites => prevSites.map(site =>
            site.id === siteItem.siteId
              ? {
                  ...site,
                  plugin_installed: result.plugin_installed,
                  plugin_version: result.plugin_version,
                  plugin_last_checked: new Date().toISOString()
                }
              : site
          ));
        } else {
          setBulkCheckModal(prev => ({
            ...prev,
            progress: prev.progress.map(item =>
              item.siteId === siteItem.siteId
                ? { ...item, status: 'error', message: 'Failed to check plugin status' }
                : item
            )
          }));
        }
      } catch (error) {
        setBulkCheckModal(prev => ({
          ...prev,
          progress: prev.progress.map(item =>
            item.siteId === siteItem.siteId
              ? { ...item, status: 'error', message: `Error: ${error.message}` }
              : item
          )
        }));
      }

      // Small delay between requests to avoid overwhelming the server
      if (i < sitesToCheck.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setBulkCheckModal(prev => ({ ...prev, checking: false }));
    
    // Reapply filters to update the view
    setTimeout(() => {
      applyFilters();
    }, 100);
  };

  const closeBulkCheckModal = () => {
    setBulkCheckModal({ open: false, progress: [], checking: false, minimized: false });
  };

  const minimizeBulkCheckModal = () => {
    setBulkCheckModal(prev => ({ ...prev, minimized: true }));
  };

  const restoreBulkCheckModal = () => {
    setBulkCheckModal(prev => ({ ...prev, minimized: false }));
  };

  // Table view component
  const renderTableView = () => (
    <>
    <TableContainer component={Paper} elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <Tooltip title="Account name and root domain" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Account</TableCell></Tooltip>
            <Tooltip title="Account status and priority level" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Status</TableCell></Tooltip>
            <Tooltip title="Assigned Client Success and Strategist" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>CS / Strategist</TableCell></Tooltip>
            <Tooltip title="SEO shirt size classification" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>SEO</TableCell></Tooltip>
            <Tooltip title="Domains, contacts, and subscriptions count" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Counts</TableCell></Tooltip>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSites.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((site) => {
            const statusColors = getStatusColors(site.account_status);
            return (
              <TableRow
                key={site.id}
                hover
                sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                onClick={() => navigate(`/sites/${site.id}/edit`)}
              >
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }} title={site.account_name || site.rootdomain}>
                    {site.account_name || site.rootdomain}
                  </Typography>
                  <Typography variant="caption" noWrap color="text.secondary" sx={{ display: 'block' }} title={site.rootdomain}>
                    {site.rootdomain}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {site.account_status ? (
                      <Tooltip title={`Account status: ${site.account_status}`} arrow>
                        <Chip
                          label={site.account_status}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 600, width: 'fit-content',
                            bgcolor: statusColors.chipBg, color: statusColors.chipColor,
                            border: '1px solid', borderColor: statusColors.chipBorder,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                    {site.priority && (
                      <Tooltip title={`Priority: ${site.priority}`} arrow>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', cursor: 'help' }}>
                          {site.priority}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: 140 }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', lineHeight: 1.3 }} title={site.assigned_cs || site.cs}>
                    {site.assigned_cs || site.cs || '—'}
                  </Typography>
                  {site.strategist && (
                    <Typography variant="caption" noWrap color="text.secondary" sx={{ display: 'block' }} title={site.strategist}>
                      {site.strategist}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {site.seo_shirt_size ? (
                    <Tooltip title={`SEO size: ${site.seo_shirt_size}${site.industry ? ` — ${site.industry}` : ''}`} arrow>
                      <Chip label={site.seo_shirt_size} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={`${site.domain_count || 0} domains`} arrow>
                      <Chip icon={<Language sx={{ fontSize: 11 }} />} label={site.domain_count || 0} size="small" variant="outlined" color="primary"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                    </Tooltip>
                    <Tooltip title={`${site.contact_count || 0} contacts`} arrow>
                      <Chip icon={<AccountCircle sx={{ fontSize: 11 }} />} label={site.contact_count || 0} size="small" variant="outlined" color="secondary"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                    </Tooltip>
                    <Tooltip title={`${site.subscription_count || 0} subscriptions`} arrow>
                      <Chip icon={<TrendingUp sx={{ fontSize: 11 }} />} label={site.subscription_count || 0} size="small" variant="outlined" color="success"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => navigate(`/sites/${site.id}/edit`)} sx={{ color: 'primary.main', p: 0.5 }} title="Edit">
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => window.open(site.website_url, '_blank')} sx={{ color: 'text.secondary', p: 0.5 }} title="Visit">
                      <Launch sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => openDeleteDialog(site)} sx={{ color: 'error.main', p: 0.5 }} title="Delete">
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      count={filteredSites.length}
      page={page}
      onPageChange={(e, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); localStorage.setItem('allSites_rowsPerPage', e.target.value); }}
      rowsPerPageOptions={[25, 50, 100]}
    />
    </>
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{ py: 4 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1"
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  WordPress Sites
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ fontWeight: 400 }}
                >
                  Manage your connected WordPress sites
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={downloadingPlugin ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
                  onClick={handleDownloadPlugin}
                  disabled={downloadingPlugin}
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {downloadingPlugin ? 'Downloading...' : 'Download Plugin'}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/add-site')}
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Add New Site
                </Button>
              </Box>
            </Box>

            {/* Search and Filter Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
              {/* Left side - Filters */}
              <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                {/* Search Bar */}
                <TextField
                  variant="outlined"
                  placeholder="Search sites by domain, URL, or account name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  sx={{
                    minWidth: 300,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={clearSearch}
                          size="small"
                          sx={{ color: 'text.secondary' }}
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Plugin Status Filter */}
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel>Plugin Status</InputLabel>
                  <Select
                    value={pluginFilter}
                    onChange={(e) => handlePluginFilter(e.target.value)}
                    label="Plugin Status"
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Sites</MenuItem>
                    <MenuItem value="installed">Plugin Installed</MenuItem>
                    <MenuItem value="not_installed">Plugin Not Installed</MenuItem>
                  </Select>
                </FormControl>

                {/* Account Status Filter */}
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel>Account Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    label="Account Status"
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {getAvailableStatuses().map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Right side - Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {(searchQuery || pluginFilter !== 'all' || statusFilter !== 'all') && (
                  <IconButton
                    onClick={clearAllFilters}
                    sx={{
                      color: 'error.main',
                      backgroundColor: 'error.light',
                      borderRadius: 2,
                      p: 1.5,
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      },
                    }}
                    title="Clear All Filters"
                  >
                    <Close />
                  </IconButton>
                )}
                
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="view mode"
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      borderRadius: 0,
                      px: 1.5,
                      py: 0.75,
                      minWidth: 'auto',
                      fontSize: '0.875rem',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <ViewModule sx={{ mr: 0.5, fontSize: 16 }} />
                    Card
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="table view">
                    <TableView sx={{ mr: 0.5, fontSize: 16 }} />
                    Table
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

          {/* Results Counter */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredSites.length === 0 
                ? (searchQuery || pluginFilter !== 'all' || statusFilter !== 'all') 
                  ? 'No sites match the current filters'
                  : 'No sites available'
                : `Showing ${Math.min(page * rowsPerPage + 1, filteredSites.length)}–${Math.min((page + 1) * rowsPerPage, filteredSites.length)} of ${filteredSites.length} accounts${filteredSites.length !== sites.length ? ` (${sites.length} total)` : ''}`
              }
            </Typography>
          </Box>

          {/* Active Filters Display */}
          {(searchQuery || pluginFilter !== 'all' || statusFilter !== 'all') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterList sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Active Filters:
                </Typography>
              </Box>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  onDelete={clearSearch}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {pluginFilter !== 'all' && (
                <Chip
                  label={`Plugin: ${pluginFilter === 'installed' ? 'Installed' : 'Not Installed'}`}
                  onDelete={() => handlePluginFilter('all')}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
              {statusFilter !== 'all' && (
                <Chip
                  label={`Status: ${statusFilter}`}
                  onDelete={() => handleStatusFilter('all')}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {/* Bulk Action Button Row */}
          {pluginFilter === 'not_installed' && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 2,
                  bgcolor: 'primary.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.main'
                }}>
                  <Extension sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="body2" color="primary.dark" sx={{ mr: 3, fontWeight: 500 }}>
                    Found {filteredSites.filter(site => site.plugin_installed === false || site.plugin_installed === null).length} sites without plugins
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleBulkPluginCheck}
                    startIcon={<PlayArrow />}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 2,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        boxShadow: 3,
                      },
                    }}
                    title="Check plugin status for all filtered sites"
                  >
                    Check All Plugin Status
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

        {filteredSites.length === 0 && !searchQuery && pluginFilter === 'all' && statusFilter === 'all' ? (
            <Card 
              elevation={0}
              sx={{ 
                border: '2px dashed',
                borderColor: 'grey.300',
                py: 8,
                textAlign: 'center'
              }}
            >
              <CardContent>
                <Language 
                  sx={{ 
                    fontSize: 64, 
                    color: 'grey.400',
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h5" 
                  color="text.primary"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  No WordPress Sites Yet
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  Connect your WordPress sites to start managing form submissions and user authentication
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={downloadingPlugin ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
                    onClick={handleDownloadPlugin}
                    disabled={downloadingPlugin}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    {downloadingPlugin ? 'Downloading...' : 'Download Plugin'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/add-site')}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Add Your First Site
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : filteredSites.length === 0 && (searchQuery || pluginFilter !== 'all' || statusFilter !== 'all') ? (
            <Card 
              elevation={0}
              sx={{ 
                border: '2px dashed',
                borderColor: 'grey.300',
                py: 6,
                textAlign: 'center'
              }}
            >
              <CardContent>
                <Search 
                  sx={{ 
                    fontSize: 48, 
                    color: 'grey.400',
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h6" 
                  color="text.primary"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  No Sites Found
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 2 }}
                >
                  No sites match the current filters
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <IconButton
                    onClick={clearAllFilters}
                    sx={{
                      color: 'error.main',
                      backgroundColor: 'error.light',
                      borderRadius: 2,
                      p: 1.5,
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      },
                    }}
                    title="Clear All Filters"
                  >
                    <Close />
                  </IconButton>
                  <Button
                    variant="outlined"
                    startIcon={downloadingPlugin ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
                    onClick={handleDownloadPlugin}
                    disabled={downloadingPlugin}
                  >
                    {downloadingPlugin ? 'Downloading...' : 'Download Plugin'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/add-site')}
                  >
                    Add New Site
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : viewMode === 'table' ? (
            renderTableView()
          ) : (
            <>
            <Grid container spacing={3}>
                {filteredSites.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((site) => (
                <Grid item xs={12} sm={6} md={6} lg={4} key={site.id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      height: '100%',  // Ensure consistent card heights
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: getStatusColors(site.account_status).bgcolor,
                      boxShadow: getStatusColors(site.account_status).boxShadow,
                      '&:hover': {
                        borderColor: 'grey.300',
                        transform: 'translateY(-4px)',
                        boxShadow: getStatusColors(site.account_status).hoverShadow
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Language 
                          sx={{ 
                            color: 'primary.main',
                            mr: 1.5,
                            mt: 0.5,
                            fontSize: 24,
                            flexShrink: 0  // Prevent icon from shrinking
                          }} 
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>  {/* minWidth: 0 allows text to shrink */}
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.2
                            }}
                            title={site.account_name || site.rootdomain}  // Show full text on hover
                          >
                            {site.account_name || site.rootdomain}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.3,
                              fontSize: '0.875rem'
                            }}
                            title={site.rootdomain}  // Show full domain on hover
                          >
                            {site.rootdomain}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box
                        sx={{
                          bgcolor: 'action.hover',
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          mb: 2
                        }}
                      >
                        {/* Last BR Date and CS Row */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: site.account_status ? 1 : 0
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Schedule sx={{ color: 'text.secondary', fontSize: 16, mr: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              Last BR: {site.date_updated ? new Date(site.date_updated).toLocaleDateString() : '—'}
                            </Typography>
                          </Box>

                          {(site.assigned_cs || site.cs) && (
                          <Chip
                            icon={<AccountCircle sx={{ fontSize: 14 }} />}
                            label={site.assigned_cs || site.cs}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }}
                          />
                          )}
                          {!(site.assigned_cs || site.cs) && site.plugin_installed !== null && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: !site.plugin_installed ? 'pointer' : 'default',
                              padding: !site.plugin_installed ? '2px 4px' : '0',
                              borderRadius: !site.plugin_installed ? 1 : 0,
                              '&:hover': !site.plugin_installed ? {
                                backgroundColor: 'rgba(211, 47, 47, 0.08)'
                              } : {}
                            }}
                            onClick={!site.plugin_installed ? () => handleCheckPlugin(site.id) : undefined}
                            title={!site.plugin_installed ? 'Click to check plugin status' : ''}
                          >
                            {checkingPlugin[site.id] ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: 'text.secondary', mr: 0.5 }}
                              />
                            ) : (
                              <Extension
                                sx={{
                                  color: site.plugin_installed ? 'success.main' : 'error.main',
                                  fontSize: 16,
                                  mr: 0.5
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              color={site.plugin_installed ? 'success.main' : 'error.main'}
                            >
                              Plugin {site.plugin_installed ? 'OK' : checkingPlugin[site.id] ? 'Checking...' : 'Missing'}
                            </Typography>
                            {!site.plugin_installed && !checkingPlugin[site.id] && (
                              <Refresh
                                sx={{
                                  color: 'error.main',
                                  fontSize: 14,
                                  ml: 0.5,
                                  opacity: 0.7
                                }} 
                              />
                            )}
                          </Box>
                        )}
                        </Box>
                        
                        {/* Account Status Row */}
                        {site.account_status && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AccountCircle 
                                sx={{ 
                                  fontSize: 14, 
                                  mr: 0.5,
                                  color: getStatusColors(site.account_status).iconColor
                                }} 
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: getStatusColors(site.account_status).iconColor
                                }}
                              >
                                Status: {site.account_status}
                              </Typography>
                            </Box>
                            <Chip
                              label={site.account_status}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: getStatusColors(site.account_status).chipBg,
                                color: getStatusColors(site.account_status).chipColor,
                                border: '1px solid',
                                borderColor: getStatusColors(site.account_status).chipBorder,
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ 
                      px: 2, 
                      pb: 2,
                      ...((!site.plugin_installed) && {
                        opacity: 0.6
                      })
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<Language sx={{ fontSize: 14 }} />}
                          label={`${site.domain_count || 0} Domains`}
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate(`/sites/${site.id}/edit`)}
                          sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
                        />
                        <Chip
                          icon={<AccountCircle sx={{ fontSize: 14 }} />}
                          label={`${site.contact_count || 0} Contacts`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate(`/sites/${site.id}/edit`)}
                          sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
                        />
                        <Chip
                          icon={<TrendingUp sx={{ fontSize: 14 }} />}
                          label={`${site.subscription_count || 0} Subs`}
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => navigate(`/sites/${site.id}/edit`)}
                          sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/sites/${site.id}/edit`)}
                        sx={{ color: 'primary.main' }}
                        title="Edit Account"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => window.open(site.website_url, '_blank')}
                        sx={{ color: 'text.secondary' }}
                        title="Visit Site"
                      >
                        <Launch />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(site)}
                        sx={{ color: 'error.main' }}
                        title="Delete Account"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
                ))}
              </Grid>
              <TablePagination
                component="div"
                count={filteredSites.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); localStorage.setItem('allSites_rowsPerPage', e.target.value); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
          )}
        </Box>
      </Container>
      </Box>

    <ConfirmDialog
      open={deleteDialog.open}
      title="Delete Site"
      message={`Are you sure you want to delete "${deleteDialog.site?.rootdomain}"? This action cannot be undone.`}
      onConfirm={() => handleDeleteSite(deleteDialog.site?.id)}
      onCancel={closeDeleteDialog}
      confirmLabel="Delete"
      cancelLabel="Cancel"
      confirmColor="error"
    />

    {/* Bulk Plugin Check Modal */}
    <Dialog
      open={bulkCheckModal.open && !bulkCheckModal.minimized}
      onClose={bulkCheckModal.checking ? undefined : closeBulkCheckModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Extension sx={{ mr: 2 }} />
            Bulk Plugin Check Progress
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={minimizeBulkCheckModal}
              size="small"
              sx={{ color: 'text.secondary' }}
              title="Minimize"
            >
              <Minimize />
            </IconButton>
            {!bulkCheckModal.checking && (
              <IconButton
                onClick={closeBulkCheckModal}
                size="small"
                sx={{ color: 'text.secondary' }}
                title="Close"
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <LinearProgress 
            variant="determinate" 
            value={bulkCheckModal.progress.length > 0 ? 
              (bulkCheckModal.progress.filter(p => p.status !== 'pending').length / bulkCheckModal.progress.length) * 100 : 0
            }
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" align="center">
            {bulkCheckModal.progress.filter(p => p.status !== 'pending').length} of {bulkCheckModal.progress.length} sites checked
          </Typography>
        </Box>
        
        <List>
          {bulkCheckModal.progress.map((progress) => (
            <ListItem key={progress.siteId}>
              <ListItemIcon>
                {progress.status === 'pending' && <CircularProgress size={20} />}
                {progress.status === 'checking' && <CircularProgress size={20} />}
                {progress.status === 'success' && (
                  progress.pluginInstalled ? 
                    <CheckCircle color="success" /> : 
                    <Extension color="disabled" />
                )}
                {progress.status === 'error' && <Error color="error" />}
              </ListItemIcon>
              <ListItemText
                primary={progress.siteName}
                secondary={progress.message}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        {!bulkCheckModal.checking && bulkCheckModal.progress.length > 0 && 
         bulkCheckModal.progress.some(p => p.status === 'pending') && (
          <Button 
            onClick={startBulkPluginCheck}
            variant="contained"
            startIcon={<PlayArrow />}
          >
            Start Checking
          </Button>
        )}
        <Button 
          onClick={closeBulkCheckModal}
          disabled={bulkCheckModal.checking}
        >
          {bulkCheckModal.checking ? 'Checking...' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Minimized Bulk Check Indicator */}
    {bulkCheckModal.open && bulkCheckModal.minimized && (
      <Fab
        onClick={restoreBulkCheckModal}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          zIndex: 1300,
        }}
        title="Restore Plugin Check Progress"
      >
        <Badge 
          badgeContent={bulkCheckModal.progress.filter(p => p.status === 'checking').length > 0 ? 
            bulkCheckModal.progress.filter(p => p.status === 'checking').length : null
          }
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: bulkCheckModal.checking ? 'warning.main' : 'success.main',
              color: 'white',
            }
          }}
        >
          {bulkCheckModal.checking ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            <Extension />
          )}
        </Badge>
      </Fab>
    )}
  </>
  );
};

export default AllSites;